import React, { useState, useMemo, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Edit3,
  ArrowRight,
  ArrowLeft,
  Search,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Flag,
  ThumbsUp,
  ThumbsDown,
  Plus,
  ArrowUpDown,
  SlidersHorizontal,
  Square,
  CheckSquare,
  X
} from 'lucide-react';
import { WorkflowCard } from '../shared/WorkflowCard';
import { DetailedClauseJSON } from '../../../../types/contract.types';
import { WorkflowContext, ClauseDecisionStatus } from '../../../../types/workflow.types';

interface OutletContext extends WorkflowContext {
  onNextStep: () => void;
  onPrevStep: () => void;
  getStepProgress: () => { reviewed: number; total: number; percentage: number };
  bulkUpdateClauseDecisions: (clauseIds: string[], status: ClauseDecisionStatus) => Promise<void>;
}

export const Step3_ClauseEditing: React.FC = () => {
  const context = useOutletContext<OutletContext>();
  const {
    analysis,
    clauseDecisions,
    selectedForNegotiation,
    isLoading,
    onNextStep,
    onPrevStep,
    updateClauseDecision,
    bulkUpdateClauseDecisions,
    toggleNegotiationSelection,
    getStepProgress
  } = context;

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterRiskLevel, setFilterRiskLevel] = useState<string>('all');
  const [sortOption, setSortOption] = useState<string>('default');
  const [selectedClause, setSelectedClause] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [multiSelectedClauses, setMultiSelectedClauses] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  const progress = getStepProgress();

  // Toggle single clause selection for multi-select
  const toggleMultiSelect = useCallback((clauseRef: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMultiSelectedClauses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clauseRef)) {
        newSet.delete(clauseRef);
      } else {
        newSet.add(clauseRef);
      }
      return newSet;
    });
  }, []);

  // Select all visible clauses (defined after filteredClauses useMemo)
  const selectAllVisible = useCallback((clauses: DetailedClauseJSON[]) => {
    const allRefs = clauses.map(c => c.reference || '').filter(Boolean);
    setMultiSelectedClauses(new Set(allRefs));
  }, []);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setMultiSelectedClauses(new Set());
  }, []);

  // Bulk action handler
  const handleBulkAction = useCallback(async (status: ClauseDecisionStatus) => {
    if (multiSelectedClauses.size === 0) return;

    setIsBulkProcessing(true);
    try {
      // Use the dedicated bulk update function
      await bulkUpdateClauseDecisions(Array.from(multiSelectedClauses), status);
      // Clear selection after bulk action
      setMultiSelectedClauses(new Set());
    } catch (error) {
      console.error('Error in bulk action:', error);
    } finally {
      setIsBulkProcessing(false);
    }
  }, [multiSelectedClauses, bulkUpdateClauseDecisions]);

  const getClauseRiskLevel = (clause: DetailedClauseJSON): string => {
    if (clause.problematic_subclauses?.length > 0) {
      // Check risk levels - valid values are High, Medium, Low
      const riskLevels = clause.problematic_subclauses.map(s =>
        (s.risk_level || '').toLowerCase()
      );
      if (riskLevels.includes('high')) return 'high';
      if (riskLevels.includes('medium')) return 'medium';
    }
    return 'low';
  };

  const getRiskPriority = (riskLevel: string): number => {
    switch (riskLevel.toLowerCase()) {
      case 'high': return 3;
      case 'medium': return 2;
      case 'low': return 1;
      default: return 0;
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'high':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
      case 'low':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800';
    }
  };

  const getStatusBadge = (status?: ClauseDecisionStatus) => {
    switch (status) {
      case 'approved':
        return { icon: <CheckCircle className="w-4 h-4" />, text: 'Approved', color: 'text-green-600 bg-green-100 dark:bg-green-900/30' };
      case 'rejected':
        return { icon: <ThumbsDown className="w-4 h-4" />, text: 'Rejected', color: 'text-red-600 bg-red-100 dark:bg-red-900/30' };
      case 'flagged':
        return { icon: <Flag className="w-4 h-4" />, text: 'Flagged', color: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30' };
      case 'needs_review':
        return { icon: <AlertCircle className="w-4 h-4" />, text: 'Needs Review', color: 'text-orange-600 bg-orange-100 dark:bg-orange-900/30' };
      default:
        return { icon: <AlertCircle className="w-4 h-4" />, text: 'Pending', color: 'text-gray-500 bg-gray-100 dark:bg-gray-800' };
    }
  };

  const filteredClauses = useMemo(() => {
    if (!analysis?.clauses) return [];

    let result = analysis.clauses.filter(clause => {
      const clauseRef = clause.reference || '';
      const clauseTitle = clause.title || '';
      const decision = clauseDecisions[clauseRef];
      const riskLevel = getClauseRiskLevel(clause);

      // Search filter - safely handle undefined values
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' ||
        clauseTitle.toLowerCase().includes(searchLower) ||
        clauseRef.toLowerCase().includes(searchLower) ||
        (clause.party_obligations || '').toLowerCase().includes(searchLower) ||
        (clause.summary || '').toLowerCase().includes(searchLower);

      // Status filter
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'pending' && (!decision || decision.status === 'pending')) ||
        (filterStatus === 'reviewed' && decision && decision.status !== 'pending') ||
        (filterStatus === 'flagged' && decision?.status === 'flagged') ||
        (filterStatus === 'negotiation' && selectedForNegotiation.includes(clauseRef));

      // Risk level filter
      const matchesRisk = filterRiskLevel === 'all' ||
        filterRiskLevel === riskLevel;

      return matchesSearch && matchesStatus && matchesRisk;
    });

    // Apply sorting
    if (sortOption !== 'default') {
      result = [...result].sort((a, b) => {
        const refA = a.reference || '';
        const refB = b.reference || '';
        const riskA = getRiskPriority(getClauseRiskLevel(a));
        const riskB = getRiskPriority(getClauseRiskLevel(b));

        switch (sortOption) {
          case 'risk-desc':
            return riskB - riskA; // High risk first
          case 'risk-asc':
            return riskA - riskB; // Low risk first
          case 'ref-asc':
            return refA.localeCompare(refB, undefined, { numeric: true });
          case 'ref-desc':
            return refB.localeCompare(refA, undefined, { numeric: true });
          default:
            return 0;
        }
      });
    }

    return result;
  }, [analysis?.clauses, searchTerm, filterStatus, filterRiskLevel, sortOption, clauseDecisions, selectedForNegotiation]);

  const handleDecision = async (clauseRef: string, status: ClauseDecisionStatus) => {
    await updateClauseDecision(clauseRef, { status, comment: commentText || undefined });
    setCommentText('');
  };

  const handleToggleNegotiation = (clauseRef: string) => {
    toggleNegotiationSelection(clauseRef);
  };

  if (isLoading || !analysis) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
        <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      </div>
    );
  }

  const selectedClauseData = selectedClause
    ? analysis.clauses?.find(c => c.reference === selectedClause)
    : null;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Clause Review
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Review and make decisions on individual clauses
          </p>
        </div>
        <button
          onClick={onNextStep}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
        >
          Continue to Summary
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="bg-white dark:bg-dark-surface rounded-xl border border-gray-200 dark:border-dark-border p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Review Progress
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {progress.reviewed} of {progress.total} clauses reviewed ({progress.percentage}%)
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-600 dark:bg-accent-500 rounded-full transition-all duration-300"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
      </div>

      {/* Search and Filter */}
      <div className="space-y-4">
        {/* Search and Sort Row */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search clauses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          {/* Sort Dropdown */}
          <div className="relative">
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="appearance-none pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer"
            >
              <option value="default">Default Order</option>
              <option value="risk-desc">Risk: High → Low</option>
              <option value="risk-asc">Risk: Low → High</option>
              <option value="ref-asc">Reference: A → Z</option>
              <option value="ref-desc">Reference: Z → A</option>
            </select>
            <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Risk Level Filter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center gap-1">
            <SlidersHorizontal className="w-4 h-4" />
            Risk Level:
          </span>
          {['all', 'high', 'medium', 'low'].map(level => (
            <button
              key={level}
              onClick={() => setFilterRiskLevel(level)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors capitalize ${
                filterRiskLevel === level
                  ? level === 'all'
                    ? 'bg-primary-600 text-white'
                    : level === 'high'
                    ? 'bg-red-600 text-white'
                    : level === 'medium'
                    ? 'bg-yellow-600 text-white'
                    : 'bg-green-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {level === 'all' ? 'All Risks' : level}
            </button>
          ))}
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2">
          {['all', 'pending', 'reviewed', 'flagged', 'negotiation'].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-2 text-sm rounded-lg transition-colors capitalize ${
                filterStatus === status
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {status === 'negotiation' ? 'For Negotiation' : status}
            </button>
          ))}
        </div>

        {/* Bulk Action Toolbar */}
        {multiSelectedClauses.size > 0 && (
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  {multiSelectedClauses.size} clause{multiSelectedClauses.size > 1 ? 's' : ''} selected
                </span>
                <button
                  onClick={clearSelection}
                  className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">Bulk Actions:</span>
                <button
                  onClick={() => handleBulkAction('approved')}
                  disabled={isBulkProcessing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-colors"
                >
                  <ThumbsUp className="w-4 h-4" />
                  Approve All
                </button>
                <button
                  onClick={() => handleBulkAction('flagged')}
                  disabled={isBulkProcessing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400 text-white rounded-lg transition-colors"
                >
                  <Flag className="w-4 h-4" />
                  Flag All
                </button>
                <button
                  onClick={() => handleBulkAction('rejected')}
                  disabled={isBulkProcessing}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors"
                >
                  <ThumbsDown className="w-4 h-4" />
                  Reject All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content - Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Clause List */}
        <WorkflowCard
          title={`Clauses (${filteredClauses.length})`}
          noPadding
          actions={
            <button
              onClick={() => {
                if (multiSelectedClauses.size === filteredClauses.length && filteredClauses.length > 0) {
                  clearSelection();
                } else {
                  selectAllVisible(filteredClauses);
                }
              }}
              className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              {multiSelectedClauses.size === filteredClauses.length && filteredClauses.length > 0 ? (
                <>
                  <CheckSquare className="w-4 h-4" />
                  Deselect All
                </>
              ) : (
                <>
                  <Square className="w-4 h-4" />
                  Select All
                </>
              )}
            </button>
          }
        >
          <div className="max-h-[600px] overflow-y-auto divide-y divide-gray-200 dark:divide-dark-border">
            {filteredClauses.map((clause, index) => {
              const clauseRef = clause.reference || `clause-${index}`;
              const decision = clauseDecisions[clauseRef];
              const riskLevel = getClauseRiskLevel(clause);
              const statusBadge = getStatusBadge(decision?.status);
              const isSelected = selectedClause === clauseRef;
              const isForNegotiation = selectedForNegotiation.includes(clauseRef);
              const isMultiSelected = multiSelectedClauses.has(clauseRef);

              return (
                <div
                  key={`${clauseRef}-${index}`}
                  className={`flex items-start gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer ${
                    isSelected ? 'bg-primary-50 dark:bg-primary-900/20 border-l-4 border-primary-500' : ''
                  } ${isMultiSelected ? 'bg-primary-25 dark:bg-primary-900/10' : ''}`}
                  onClick={() => setSelectedClause(clauseRef)}
                >
                  {/* Checkbox */}
                  <button
                    onClick={(e) => toggleMultiSelect(clauseRef, e)}
                    className={`flex-shrink-0 w-5 h-5 rounded border-2 transition-colors flex items-center justify-center ${
                      isMultiSelected
                        ? 'bg-primary-600 border-primary-600 text-white'
                        : 'border-gray-300 dark:border-gray-600 hover:border-primary-500'
                    }`}
                  >
                    {isMultiSelected && <CheckCircle className="w-4 h-4" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRiskColor(riskLevel)}`}>
                        {riskLevel}
                      </span>
                      {isForNegotiation && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                          Negotiation
                        </span>
                      )}
                    </div>
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {clause.reference || `Clause ${index + 1}`}{clause.title ? ` - ${clause.title}` : ''}
                    </p>
                    {clause.summary && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                        {clause.summary}
                      </p>
                    )}
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${statusBadge.color}`}>
                    {statusBadge.icon}
                    <span className="hidden sm:inline">{statusBadge.text}</span>
                  </div>
                </div>
              );
            })}

            {filteredClauses.length === 0 && (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                No clauses match your search or filter criteria
              </div>
            )}
          </div>
        </WorkflowCard>

        {/* Clause Detail */}
        <WorkflowCard
          title={selectedClauseData ? `${selectedClauseData.reference} - ${selectedClauseData.title}` : 'Select a Clause'}
        >
          {selectedClauseData ? (
            <div className="space-y-6">
              {/* Risk and Status */}
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${getRiskColor(getClauseRiskLevel(selectedClauseData))}`}>
                  {getClauseRiskLevel(selectedClauseData) === 'high' ? <AlertTriangle className="w-4 h-4" /> :
                   getClauseRiskLevel(selectedClauseData) === 'medium' ? <AlertCircle className="w-4 h-4" /> :
                   <CheckCircle className="w-4 h-4" />}
                  {getClauseRiskLevel(selectedClauseData).charAt(0).toUpperCase() + getClauseRiskLevel(selectedClauseData).slice(1)} Risk
                </span>
                {selectedClauseData.negotiation_analysis?.classification && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                    {selectedClauseData.negotiation_analysis.classification}
                  </span>
                )}
              </div>

              {/* Party Obligations */}
              {selectedClauseData.party_obligations && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Party Obligations
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    {selectedClauseData.party_obligations}
                  </p>
                </div>
              )}

              {/* Issues */}
              {selectedClauseData.problematic_subclauses && selectedClauseData.problematic_subclauses.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Issues Identified
                  </h4>
                  <div className="space-y-2">
                    {selectedClauseData.problematic_subclauses.map((subclause, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${getRiskColor(subclause.risk_level)}`}>
                        <p className="font-medium text-sm">{subclause.subref}: {subclause.issue}</p>
                        <p className="text-xs mt-1 opacity-80">{subclause.note}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {selectedClauseData.actionable_recommendations && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Recommendations
                  </h4>
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                    {selectedClauseData.actionable_recommendations.immediate_actions && (
                      <p><strong>Immediate:</strong> {selectedClauseData.actionable_recommendations.immediate_actions}</p>
                    )}
                    {selectedClauseData.actionable_recommendations.approvals_needed && (
                      <p><strong>Approvals:</strong> {selectedClauseData.actionable_recommendations.approvals_needed}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Comment Input */}
              <div>
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 block">
                  Add Comment (Optional)
                </label>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a note about this clause..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-dark-border">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleDecision(selectedClause!, 'approved')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      clauseDecisions[selectedClause!]?.status === 'approved'
                        ? 'bg-green-600 text-white'
                        : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                    }`}
                  >
                    <ThumbsUp className="w-4 h-4" />
                    Accept
                  </button>
                  <button
                    onClick={() => handleDecision(selectedClause!, 'flagged')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      clauseDecisions[selectedClause!]?.status === 'flagged'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}
                  >
                    <Flag className="w-4 h-4" />
                    Flag for Review
                  </button>
                  <button
                    onClick={() => handleDecision(selectedClause!, 'rejected')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      clauseDecisions[selectedClause!]?.status === 'rejected'
                        ? 'bg-red-600 text-white'
                        : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                    }`}
                  >
                    <ThumbsDown className="w-4 h-4" />
                    Reject
                  </button>
                </div>

                <button
                  onClick={() => handleToggleNegotiation(selectedClause!)}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    selectedForNegotiation.includes(selectedClause!)
                      ? 'bg-purple-600 text-white'
                      : 'bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-400'
                  }`}
                >
                  <Plus className="w-4 h-4" />
                  {selectedForNegotiation.includes(selectedClause!)
                    ? 'Remove from Negotiation'
                    : 'Add to Negotiation List'}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Edit3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Select a clause from the list to review</p>
            </div>
          )}
        </WorkflowCard>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onPrevStep}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Analysis
        </button>
        <button
          onClick={onNextStep}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
        >
          Continue to Summary
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
